import { ChevronLeft } from "lucide-react";

import LoadingButton from "@/components/LoadingButton";
import { StepHeader } from "@/components/onboarding/StepHeader";
import { Button } from "@/components/ui/button";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { api, cn, handleApiRequest } from "@/lib/utils";

import type { CompleteOnboardingFormData, OnboardingStepProps } from "@/types/onboarding";

const REBOLT_GOALS = [
  {
    value: "leads",
    label: "Get more leads",
  },
  {
    value: "jobs-and-scheduling",
    label: "Manage jobs and scheduling",
  },
  {
    value: "payments",
    label: "Accept payments",
  },
  {
    value: "website",
    label: "Build a professional website",
  },
  {
    value: "social-media",
    label: "Increase social media reach",
  },
  {
    value: "profitability",
    label: "Be more profitable",
  },
];

export function ReboltGoalsStep({ onBack, canGoBack, isOptional, onNext, form }: OnboardingStepProps) {
  const selectedGoals = form.watch("reboltGoals");

  const isGoalSelected = (goalValue: string): boolean => selectedGoals.includes(goalValue);

  const handleToggleGoal = (goalValue: string): void => {
    const updatedGoals = isGoalSelected(goalValue)
      ? selectedGoals.filter((g) => g !== goalValue)
      : [...selectedGoals, goalValue];

    form.setValue("reboltGoals", updatedGoals, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const handleContinue = async (websiteId: string | undefined, data: CompleteOnboardingFormData) => {
    if (!websiteId) {
      return;
    }
    await handleApiRequest({
      makeRequest: async () => {
        const response = await api.put(`/api/sites/${websiteId}/extra-info`, data);
        return response.data.extraInfo;
      },
      successCallback: () => {
        onNext();
      },
    });
  };

  return (
    <div className="h-full flex flex-col justify-start lg:justify-center px-5 lg:px-40 py-6 lg:py-0">
      {canGoBack && (
        <Button
          type="button"
          variant="rebolt-outline"
          size="sm"
          onClick={onBack}
          className="tracking-tight w-fit lg:mb-12 mb-4 gap-2 p-2.5 lg:px-6 lg:py-2"
          iconPosition="start"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="hidden lg:inline-block">Back</span>
        </Button>
      )}
      <StepHeader
        title="Where can Rebolt help the most?"
        subtitle="Whether it's getting more leads, managing your schedule, or following up with important customer moments, we've got a solution for you."
      />

      <div className="mt-6 lg:mt-12 w-full flex flex-col lg:gap-9 gap-6">
        <div className="flex flex-wrap gap-2">
          {REBOLT_GOALS.map((goal) => (
            <div key={goal.value}>
              <Button
                type="button"
                variant="rebolt-default"
                size="lg"
                onClick={() => handleToggleGoal(goal.value)}
                className={cn(
                  "text-sm transition-colors duration-200 font-semibold lg:px-4 lg:py-3 px-3 py-1.5 bg-white",
                  isGoalSelected(goal.value)
                    ? "text-white bg-rebolt-500 hover:bg-rebolt-500/80"
                    : "text-ash-500 hover:bg-ash-100"
                )}
              >
                {goal.label}
              </Button>
            </div>
          ))}
        </div>
        <FormField
          control={form.control}
          name="otherGoals"
          render={({ field }) => (
            <FormItem className="flex flex-col mb-0">
              <FormLabel className="text-ash-700 font-semibold lg:text-base text-sm">
                Other goals <span className="text-ash-500 text-xs ml-0.5">Optional</span>
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="text"
                  placeholder="Other goals..."
                  className="text-sm mb-0 h-fit px-4 py-3 rounded-xl border-ash-300 bg-white"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div className="flex gap-4 lg:mt-12 mt-auto">
        <LoadingButton
          type="button"
          variant="rebolt-secondary"
          size="md"
          className="justify-center lg:max-w-fit tracking-tight lg:py-3 py-2"
          onClick={async () => {
            await handleContinue(form.getValues("reboltWebsiteId"), form.getValues());
          }}
        >
          Continue
        </LoadingButton>
        {isOptional && (
          <Button type="button" variant="rebolt-link" size="link" className="text-sm lg:text-base" onClick={onNext}>
            Skip for now
          </Button>
        )}
      </div>
    </div>
  );
}
